/*!
 * grid.js — grid(): the stage in columns and rows (stageGrid.js)
 * © 2026 Rob Canning — GPLv3
 *
 *     grid(3, 2)                      3 columns, 2 rows; viz(s1, cell:4) puts a box in one
 *     grid(4)                         4 by 4
 *     grid(rows:3)                    3 rows, one column; grid(cols:4) 4 columns
 *     grid(cols:[2, 1, 1], rows:2)    the first column twice as wide
 *     grid(3, 2, gap:0.02)            a gap between the cells, a share of the stage
 *     grid(3, 2, show:1)              the cells drawn, faint, numbered; show:0 not
 *     grid(shuffle)                   everything in a cell to another, at random
 *     grid(turn)                      everything one cell on; turn:-1 back, turn:2 ...
 *     grid(shuffle, trig:every(4, beat.hit))   ... every bar (any of these lines can be)
 *     grid(off)                       no grid: things stay where they are
 *
 * A new grid puts everything in its cell again: what was in cell 5 of a
 * 3 x 2 is in cell 5 of a 4 x 4 (round to the start when there are fewer).
 */

import { setGrid, clearGrid, hasGrid, gridSize, cells, shuffleCells, turnCells } from "../system/stageGrid.js";
import { stageBand } from "../system/viewBand.js";
import { placeGridded } from "./viz.js";
import { onReset } from "../system/teardown.js";

const GUIDES_ID = "oscilla-grid-guides";
let showing = false;

const arg = (ast, name) => ast?.args?.find(a => a.type === name)?.value;

/** The stage's uncovered band, in px of the window. */
const band = stageBand;

/** The cells, drawn faint and numbered -- for finding cell 4 -- or taken away. */
function drawGuides() {
  document.getElementById(GUIDES_ID)?.remove();
  if (!showing || !hasGrid()) return;
  const b = band();
  const layer = document.createElement("div");
  layer.id = GUIDES_ID;
  layer.style.cssText = `position:fixed;left:${b.left}px;top:${b.top}px;width:${b.width}px;height:${b.height}px;pointer-events:none;z-index:5`;
  for (const c of cells()) {
    const cell = document.createElement("div");
    cell.style.cssText = `position:absolute;left:${c.x * 100}%;top:${c.y * 100}%;width:${c.w * 100}%;height:${c.h * 100}%;`
      + "box-sizing:border-box;border:1px dashed rgba(128,128,128,0.45);"
      + "font:12px ui-monospace,monospace;color:rgba(128,128,128,0.8);padding:4px 6px";
    cell.textContent = String(c.n);
    layer.append(cell);
  }
  document.body.append(layer);
}

addEventListener("resize", () => { if (showing) drawGuides(); });

export function handleGridCue(ast) {
  if (arg(ast, "off") != null) {
    clearGrid();
    showing = false;
    drawGuides();
    return;
  }
  const show = arg(ast, "show");
  if (show != null) showing = !/^(0|off|none|false)$/i.test(String(show));

  const cols = arg(ast, "cols"), rows = arg(ast, "rows"), gap = arg(ast, "gap");
  if (cols != null || rows != null || gap != null) {
    const now = gridSize();
    setGrid({
      cols: cols ?? (rows != null ? 1 : now?.cols ?? 1),
      rows: rows ?? (cols != null ? 1 : now?.rows ?? 1),
      gap: gap ?? 0,
    });
  }
  if (!hasGrid()) {
    if (arg(ast, "shuffle") != null || arg(ast, "turn") != null || show != null) console.warn("[grid] no grid yet: grid(3, 2) first");
    return;
  }
  if (arg(ast, "shuffle") != null) shuffleCells();
  const turn = arg(ast, "turn");
  if (turn != null) turnCells(turn === true ? 1 : Number(turn) || 1);
  placeGridded();
  drawGuides();
}

/** The grid gone, and its lines (a new example, a new session: liveExamples.js clearStage). */
export function clearStageGrid() {
  clearGrid();
  showing = false;
  drawGuides();
}
onReset("project", clearStageGrid);
